import React, { forwardRef, useMemo, useState } from 'react';
import { bool, func, node, oneOfType, shape, string } from 'prop-types';

import { styCheckboxWrapper, styCheckboxOuter, styCheckboxInput, styCheckboxInner } from './style';

const InputCheckbox = forwardRef((props, ref) => {
  const {
    id,
    name,
    children,
    className,
    defaultValue,
    disabled,
    error,
    onChange,
    style,
    value: propsValue,
    ...inputProps
  } = props;

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== null, [propsValue]);
  const [stateValue, setStateValue] = useState(defaultValue);

  const handleChange = () => {
    if (disabled) return;

    const event = { ...new Event('change') };
    const newValue = !(isControlled ? propsValue : stateValue);
    event.target = { value: newValue, checked: newValue };
    onChange(event);

    if (!isControlled) setStateValue(event.target.checked);
  };

  const handleOuterKeyDown = (event) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      handleChange();
    }
  };

  const styProps = {
    checked: isControlled ? propsValue : stateValue,
    disabled,
  };

  return (
    <label htmlFor={id} className={`${className} ${styCheckboxWrapper(styProps)}`} style={style}>
      <span className={styCheckboxOuter} tabIndex="0" onKeyDown={handleOuterKeyDown}>
        <input
          ref={ref}
          id={id}
          data-testid={id}
          name={name}
          type="checkbox"
          className={styCheckboxInput}
          // Component is always controlled
          // If value is provided, use the provided value. Otherwise, use value stored on state
          {...(isControlled
            ? { value: propsValue, checked: propsValue }
            : { value: stateValue, checked: stateValue })}
          // Do not pass defaultValue
          onChange={handleChange}
          disabled={disabled}
          readOnly={disabled}
          {...inputProps}
        />
        <span className={styCheckboxInner(styProps)} />
      </span>
      <span>{children}</span>
    </label>
  );
});

InputCheckbox.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  children: oneOfType([node, string]),
  className: string,
  defaultValue: bool,
  disabled: bool,
  error: string,
  onChange: func,
  style: shape({}),
  value: bool,
};

InputCheckbox.defaultProps = {
  children: null,
  className: '',
  defaultValue: false,
  disabled: false,
  error: '',
  onChange: () => {},
  style: {},
  value: null,
};

export default InputCheckbox;
